import type { JSONContent } from '@tiptap/vue-3'
import type { Member } from '@/core/models/member/member'

export type LessonPerspectiveKey = 'self' | 'teacher'

export interface LessonScaleItem {
  /** Spread evenly over 0…1 in CMS order. */
  readonly value: number
  readonly label: string
  readonly selectedImage: string | null
  readonly unselectedImage: string | null
}

/** The scale one perspective of a Lesson answers Indikatoren on. */
export interface LessonScale {
  readonly perspectiveKey: LessonPerspectiveKey
  readonly items: ReadonlyArray<LessonScaleItem>
}

export interface TeacherAssessmentIndikator {
  readonly id: string
  /** Worded for the Lehrperson. */
  readonly text: string
}

export interface TeacherAssessmentKompetenzstufe {
  readonly id: string
  readonly code: string
  readonly text: string
  readonly indikatoren: ReadonlyArray<TeacherAssessmentIndikator>
}

export interface TeacherAssessmentKompetenz {
  readonly id: string
  readonly code: string
  readonly title: string
  readonly kompetenzbereich: { readonly key: string; readonly label: string }
  /** Set when Indikatoren are assigned directly to the Kompetenz, not via a Stufe. */
  readonly indikatoren?: ReadonlyArray<TeacherAssessmentIndikator>
  readonly kompetenzstufen: ReadonlyArray<TeacherAssessmentKompetenzstufe>
}

/** Indikatoren the teacher answers: those on the Kompetenz, then those on its Stufen. */
export function indikatorenForKompetenz(
  kompetenz: TeacherAssessmentKompetenz,
): ReadonlyArray<TeacherAssessmentIndikator> {
  return [
    ...(kompetenz.indikatoren ?? []),
    ...kompetenz.kompetenzstufen.flatMap((stufe) => stufe.indikatoren),
  ]
}

export interface IndikatorAnswer {
  readonly indikatorId: string
  /** A point on the scale; null is Not observed. */
  readonly value: number | null
}

export interface TeacherAssessmentReflection {
  readonly reflexionQuestionId: string
  readonly text: unknown
}

export interface TeacherAssessmentMember {
  readonly id: string
  readonly firstName?: string
  readonly lastName?: string
  readonly email?: string
  readonly pending: boolean
  readonly canLogin: boolean
  readonly lastUpdatedAvatar?: string
}

export interface MemberTeacherAssessment {
  readonly member: TeacherAssessmentMember
  /** The teacher's assessment of the member, in the order the Indikatoren are shown. */
  readonly teacherAssessment: {
    readonly given: boolean
    readonly answers: ReadonlyArray<IndikatorAnswer>
  }
  /** What the member answered about themselves, beside it. */
  readonly selfAssessment: {
    readonly given: boolean
    readonly answers: ReadonlyArray<IndikatorAnswer>
    readonly reflections: ReadonlyArray<TeacherAssessmentReflection>
  }
}

/**
 * The teacher's assessment page of one Lesson: what it asks, as fixed when its
 * feedback started, and per member the teacher's assessment beside the member's own.
 */
export interface TeacherAssessment {
  readonly lessonId: string
  readonly teams: ReadonlyArray<{
    readonly id: string
    readonly name: string
  }>
  readonly title: string
  readonly endedAt: string | null
  readonly teacherScale: LessonScale | null
  readonly selfScale: LessonScale | null
  /** Überfachliche Kompetenz, with Indikatoren directly or on a Kompetenzstufe. */
  readonly kompetenzen: ReadonlyArray<TeacherAssessmentKompetenz>

  readonly members: ReadonlyArray<MemberTeacherAssessment>
}

export function teacherAssessmentMemberAsMember(member: TeacherAssessmentMember): Member {
  const base = {
    id: member.id,
    email: member.email,
    canLogin: member.canLogin,
    lastUpdatedAvatar: member.lastUpdatedAvatar,
  }
  if (member.pending || member.firstName == null) {
    return { ...base, pending: true }
  }
  return {
    ...base,
    pending: false,
    firstName: member.firstName,
    lastName: member.lastName,
  }
}

/** One click's worth is enough: each answer replaces only that Indikator. */
export interface GiveTeacherAssessmentBody {
  readonly assessments: ReadonlyArray<{
    readonly memberId: string
    readonly answers: ReadonlyArray<{
      readonly indikatorId: string
      readonly value: number | null
    }>
  }>
}

export function asReflectionContent(
  text: unknown,
): { kind: 'json'; value: JSONContent } | { kind: 'text'; value: string } | undefined {
  if (typeof text === 'string' && text.trim()) {
    return { kind: 'text', value: text }
  }
  if (typeof text === 'object' && text !== null && 'type' in text) {
    return { kind: 'json', value: text as JSONContent }
  }
  return undefined
}
